import React from "react";
import { Link } from "react-router-dom";
import { m, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";

const menuVariants = {
  hidden: {
    opacity: 0,
    x: "100%",
    transition: { duration: 0.4, ease: "easeInOut" },
  },
  visible: {
    opacity: 1,
    x: 0,
    transition: { duration: 0.4, ease: "easeInOut" },
  },
  exit: {
    opacity: 0,
    x: "100%",
    transition: { duration: 0.4, ease: "easeInOut" },
  },
};

const AnimatedUnderlineLink = ({ children }) => (
  <span className="relative group">
    {children}
    <span
      className="absolute left-0 -bottom-0.5 w-0 h-[2px] bg-black transition-all duration-300 group-hover:w-full"
      style={{ borderRadius: 2 }}
    />
  </span>
);

const NAV_MENU = [
  { key: "home", path: "/", children: [] },
  {
    key: "about",
    path: "/about",
    children: [
      { key: "overview", path: { pathname: "/about", hash: "#overview" } },
      { key: "clients", path: { pathname: "/about", hash: "#clients" } },
      {
        key: "certification",
        path: { pathname: "/about", hash: "#certifications" },
      },
    ],
  },
  {
    key: "services",
    path: "/services",
    children: [
      { key: "buy", path: "/service/buy" },
      { key: "rent", path: "/service/rent" },
    ],
  },
  {
    key: "products",
    path: "/product",
    children: [
      { key: "arcticstore", path: "/product/arcticstore" },
      { key: "ultrafreezer", path: "/product/ultrafreezer" },
      { key: "arcticblast", path: "/product/arcticblast" },
      { key: "arcticsuperstore", path: "/product/arcticsuperstore" },
    ],
  },
  {
    key: "contact",
    path: "/contact-us",
    children: [
      { key: "whatsapp", path: "https://wa.me/6285280050713", external: true },
      { key: "email", path: "mailto:info@company.com", external: true },
      {
        key: "directions",
        path: "https://www.google.com/maps/dir//The+Kensington+Office+Tower+East+Kelapa+Gading+Kelapa+Gading+North+Jakarta+City,+Jakarta+14240/@-6.1729683,106.897147,16z/data=!4m5!4m4!1m0!1m2!1m1!1s0x2e69f50035e5ee93:0x4e0923ee7daf063e",
        external: true,
      },
      { key: "requestForm", path: "/contact-us#request-form" },
    ],
  },
  { key: "blogs", path: "/blogs", children: [] },
];

const LEFT_MENU = NAV_MENU.slice(0, 4);
const RIGHT_MENU = NAV_MENU.slice(4);

const HamburgerMenu = ({ open, onClose }) => {
  const { t, i18n } = useTranslation("nav");
  const lang = i18n.language === "id" ? "id" : "en";

  // Helper to prefix internal paths with current lang
  const withLang = (p) => {
    if (!p) return `/${lang}/`;
    // keep externals as-is
    if (typeof p === "string") {
      return p.startsWith("http") || p.startsWith("mailto:")
        ? p
        : `/${lang}${p}`;
    }
    // object form: { pathname, hash, search }
    const { pathname = "", hash, search } = p || {};
    return { pathname: `/${lang}${pathname}`, hash, search };
  };

  return (
    <AnimatePresence>
      {open && (
        <m.div
          className="fixed top-0 right-0 h-full z-50 w-full bg-white md:w-[50vw] md:left-auto md:right-0 md:rounded-l-3xl md:shadow-2xl px-6 md:px-12 py-10 flex justify-center items-start overflow-y-auto"
          style={{ willChange: "transform, opacity" }}
          initial="hidden"
          animate="visible"
          exit="exit"
          variants={menuVariants}
        >
          {/* Close button */}
          <button
            onClick={onClose}
            className="absolute top-8 right-8 w-12 h-12 rounded-full bg-black flex items-center justify-center"
            aria-label={t("close")}
          >
            <span className="text-white text-2xl">×</span>
          </button>

          {/* Two columns */}
          <div className="w-full max-w-4xl flex flex-col md:flex-row gap-x-20 gap-y-4">
            {/* Left */}
            <div className="flex-1 flex flex-col gap-4">
              {LEFT_MENU.map((col) => (
                <div key={col.key}>
                  {col.key === "services" ? (
                    <span className="block text-3xl font-extrabold text-black mb-1 px-1 cursor-default">
                      {t(`headings.${col.key}`)}
                    </span>
                  ) : (
                    <Link
                      to={withLang(col.path)}
                      className="block text-3xl font-extrabold text-black mb-1 px-1"
                      onClick={onClose}
                    >
                      <AnimatedUnderlineLink>
                        {t(`headings.${col.key}`)}
                      </AnimatedUnderlineLink>
                    </Link>
                  )}

                  {!!col.children?.length && (
                    <ul className="pl-1 mt-1 space-y-0.5">
                      {col.children.map((item) => (
                        <li key={item.key}>
                          {item.external ? (
                            <a
                              href={item.path}
                              target="_blank"
                              rel="noopener noreferrer"
                              className={`block text-lg text-black font-normal px-1${item.key === "whatsapp" ? " btn-rentetan" : ""}`}
                            >
                              <AnimatedUnderlineLink>
                                {t(`items.${item.key}`)}
                              </AnimatedUnderlineLink>
                            </a>
                          ) : (
                            <Link
                              to={withLang(item.path)}
                              className="block text-lg text-black font-normal px-1"
                              onClick={onClose}
                            >
                              <AnimatedUnderlineLink>
                                {t(`items.${item.key}`)}
                              </AnimatedUnderlineLink>
                            </Link>
                          )}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}
            </div>

            {/* Right */}
            <div className="flex-1 flex flex-col gap-4">
              {RIGHT_MENU.map((col) => (
                <div key={col.key}>
                  <Link
                    to={withLang(col.path)}
                    className="block text-3xl font-extrabold text-black mb-1 px-1"
                    onClick={onClose}
                  >
                    <AnimatedUnderlineLink>
                      {t(`headings.${col.key}`)}
                    </AnimatedUnderlineLink>
                  </Link>

                  {!!col.children?.length && (
                    <ul className="pl-1 mt-1 space-y-0.5">
                      {col.children.map((item) => (
                        <li key={item.key}>
                          {item.external ? (
                            <a
                              href={item.path}
                              target="_blank"
                              rel="noopener noreferrer"
                              className={`block text-lg text-black font-normal px-1${item.key === "whatsapp" ? " btn-rentetan" : ""}`}
                            >
                              <AnimatedUnderlineLink>
                                {t(`items.${item.key}`)}
                              </AnimatedUnderlineLink>
                            </a>
                          ) : (
                            <Link
                              to={withLang(item.path)}
                              className="block text-lg text-black font-normal px-1"
                              onClick={onClose}
                            >
                              <AnimatedUnderlineLink>
                                {t(`items.${item.key}`)}
                              </AnimatedUnderlineLink>
                            </Link>
                          )}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}
            </div>
          </div>
        </m.div>
      )}
    </AnimatePresence>
  );
};

export default HamburgerMenu;
